// services/LoginService.ts
import axios from "axios";
import * as CONSTANTS from "../utils/constants/constants";
import { Credentials } from "../interfaces/user/Credentials";
import { Token } from "../interfaces/user/Token";

type SSOConfigResponse = {
  organization_id: string;
  organization_name: string;
  login_domain: string;
  sso_enabled: boolean;
  sso_server: {
    server_id: string;
    server_name: string;
    server_url: string;
    oidc: {
      issuer?: string;
      client_id?: string;
      authorization_endpoint?: string;
      token_endpoint?: string;
      userinfo_endpoint?: string;
      jwks_uri?: string;
      scope?: string;
      response_type?: string;
      redirect_uri?: string;
      use_pkce?: boolean;
      end_session_endpoint?: string;
      post_logout_redirect_uri?: string;
    };
  } | null;
};

type SSOLinkResponse = {
  linked: boolean;
  link: {
    provider: string;
    issuer: string;
    email: string;
    subject: string;
    organization: string;
    linked_at: string;
  } | null;
};

type SSOFlowMode = "login" | "link";

const SSO_TX_STORAGE_KEY = "sso_auth_transaction";

function randomString(length: number): string {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const bytes = new Uint8Array(length);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map((value) => chars[value % chars.length])
    .join("");
}

function base64UrlEncode(arrayBuffer: ArrayBuffer): string {
  const bytes = new Uint8Array(arrayBuffer);
  let str = "";
  bytes.forEach((byte) => {
    str += String.fromCharCode(byte);
  });
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function createCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await window.crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(digest);
}

export class LoginService {
  static async login(credentials: Credentials): Promise<Token | null> {
    try {
      const response = await axios.post<Token>(
        CONSTANTS.HOST + "api/o/token/",
        credentials,
      );
      return response.data;
    } catch (err: any) {
      console.error(err);
      return null;
    }
  }

  static logout() {
    // Optionally, invalidate the server-side session or token
    return axios
      .post(CONSTANTS.HOST + "auth/logout/")
      .then((response) => {
        console.log("Logged out on server side.");
      })
      .catch((error) => {
        console.error("Error logging out on server side:", error);
      });
  }

  static async getSSOConfigByEmail(
    email: string,
    mode: SSOFlowMode = "login",
  ): Promise<SSOConfigResponse | null> {
    try {
      const response = await axios.get<SSOConfigResponse>(
        CONSTANTS.HOST + "api/v1/sso/config/",
        { params: { email, mode } },
      );
      return response.data;
    } catch (error) {
      console.error("Failed to fetch SSO config:", error);
      return null;
    }
  }

  static async startSSOFlow(
    config: SSOConfigResponse,
    email: string,
    mode: SSOFlowMode,
  ): Promise<void> {
    if (
      !config.sso_enabled ||
      !config.sso_server?.oidc?.authorization_endpoint ||
      !config.sso_server?.oidc?.client_id
    ) {
      throw new Error("SSO is not configured for this organization");
    }

    const verifier = randomString(96);
    const challenge = await createCodeChallenge(verifier);
    const state = randomString(48);
    const nonce = randomString(48);
    const redirectUri =
      config.sso_server.oidc.redirect_uri ||
      `${window.location.origin}${process.env.PUBLIC_URL || ""}/login/sso/callback`;

    sessionStorage.setItem(
      SSO_TX_STORAGE_KEY,
      JSON.stringify({
        mode,
        state,
        nonce,
        verifier,
        email,
        organization_id: config.organization_id,
        redirect_uri: redirectUri,
      }),
    );

    const params = new URLSearchParams();
    params.set("client_id", config.sso_server.oidc.client_id);
    params.set("redirect_uri", redirectUri);
    params.set("response_type", config.sso_server.oidc.response_type || "code");
    params.set("scope", config.sso_server.oidc.scope || "openid profile email");
    params.set("state", state);
    params.set("nonce", nonce);
    params.set("login_hint", email);
    params.set("code_challenge", challenge);
    params.set("code_challenge_method", "S256");

    window.location.assign(
      `${config.sso_server.oidc.authorization_endpoint}?${params.toString()}`,
    );
  }

  static getStoredSSOTransaction(): any | null {
    const raw = sessionStorage.getItem(SSO_TX_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  static clearStoredSSOTransaction(): void {
    sessionStorage.removeItem(SSO_TX_STORAGE_KEY);
  }

  static async exchangeSSOAuthorizationCode(payload: {
    authorization_code: string;
    code_verifier: string;
    organization_id: string;
    redirect_uri: string;
    email?: string;
  }): Promise<Token | null> {
    try {
      const response = await axios.post<Token>(
        CONSTANTS.HOST + "api/v1/sso/token/",
        payload,
      );
      return response.data;
    } catch (error) {
      console.error("SSO token exchange failed:", error);
      return null;
    }
  }

  static async getMySSOLink(
    accessToken: string,
  ): Promise<SSOLinkResponse | null> {
    try {
      const response = await axios.get<SSOLinkResponse>(
        CONSTANTS.HOST + "api/v1/sso/link/",
        {
          headers: { authorization: "Bearer " + accessToken },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Failed to fetch SSO link:", error);
      return null;
    }
  }

  static async completeMySSOLink(
    accessToken: string,
    payload: {
      authorization_code: string;
      code_verifier: string;
      redirect_uri: string;
    },
  ): Promise<SSOLinkResponse | null> {
    try {
      const response = await axios.post<SSOLinkResponse>(
        CONSTANTS.HOST + "api/v1/sso/link/complete/",
        payload,
        {
          headers: { authorization: "Bearer " + accessToken },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Failed to complete SSO link:", error);
      return null;
    }
  }

  static async unlinkMySSO(accessToken: string): Promise<boolean> {
    try {
      await axios.delete(CONSTANTS.HOST + "api/v1/sso/link/", {
        headers: { authorization: "Bearer " + accessToken },
      });
      return true;
    } catch (error) {
      console.error("Failed to unlink SSO account:", error);
      return false;
    }
  }
}
